import { useState, useEffect } from 'react';
import { Star, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';

interface Testimonial {
  id: string;
  name: string;
  rating: number;
  comment: string;
  service_type: string | null;
  created_at: string;
}

export function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    comment: '',
    service_type: ''
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const { data, error } = await supabase
        .from('testimonials_2026_03_20')
        .select('*')
        .eq('is_approved', true)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(false);

    try {
      const { error } = await supabase
        .from('testimonials_2026_03_20')
        .insert({
          name: formData.name,
          email: formData.email || null,
          rating: formData.rating,
          comment: formData.comment,
          service_type: formData.service_type || null,
          is_approved: false // Admin mora da odobri
        });

      if (error) throw error;

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        rating: 5,
        comment: '',
        service_type: ''
      });

      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      alert('Došlo je do greške. Molimo pokušajte ponovo.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <section id="testimonials" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Utisci Naših Klijenata
          </h2>
          <p className="text-muted-foreground text-lg">
            Pročitajte šta kažu zadovoljni korisnici naših usluga
          </p>
        </div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          </div>
        ) : testimonials.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                    <div className="flex gap-1">
                      {renderStars(testimonial.rating)}
                    </div>
                  </div>
                  {testimonial.service_type && (
                    <CardDescription className="text-xs">
                      {testimonial.service_type}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground italic">
                    "{testimonial.comment}"
                  </p>
                  <p className="text-xs text-muted-foreground mt-4">
                    {new Date(testimonial.created_at).toLocaleDateString('sr-RS')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            Budite prvi koji će ostaviti utisak!
          </div>
        )}

        {/* Forma za dodavanje utiska */}
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Ostavite Vaš Utisak</CardTitle>
            <CardDescription>
              Podelite svoje iskustvo sa našim uslugama
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Ime i Prezime *
                  </label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Vaše ime"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Email (opciono)
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="vas@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Ocena *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating })}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          rating <= formData.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300 hover:text-yellow-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Usluga (opciono)
                </label>
                <Input
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  placeholder="Npr: Natalna Karta, Sinastija..."
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Vaš Komentar *
                </label>
                <Textarea
                  required
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  placeholder="Podelite vaše iskustvo..."
                  rows={4}
                />
              </div>

              {success && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800">
                    ✅ Hvala! Vaš utisak je poslat i biće prikazan nakon odobrenja.
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Šaljem...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Pošalji Utisak
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Vaš utisak će biti pregledan i objavljen u najkraćem roku.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}